import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AddressAccessControlSelectorDto } from '@shared/src/components/accesscontrol/address/shared.address.accesscontrol.selector.component';
import { AccessControlController } from '@shared/src/controllers/accesscontrol/accesscontrol.controller';
import { HomeController } from '@shared/src/controllers/home/home.controller';
import { UserSettingController } from '@shared/src/controllers/user/usersetting.controller';
import { HLong } from '@shared/src/datatypes/HLong';
import { HString } from '@shared/src/datatypes/HString';
import { ForkliftDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftDto';
import { ForkliftFilterDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftFilterDto';
import { ForkliftScreenDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftScreenDto';
import { AccessControlGatePlateDto } from '@shared/src/dtos/accesscontrol/gate/plate/AccessControlGatePlateDto';
import { OptionDto } from '@shared/src/dtos/maintenance/option/OptionDto';
import { OptionsStripDto } from '@shared/src/dtos/maintenance/option/OptionsStripDto';
import { SharedAsideFactory } from '@shared/src/public-api';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'shared-forklift',
    templateUrl: './shared.forklift.component.html',
    styleUrls: ['./shared.forklift.component.scss']
})
export class SharedForkliftComponent implements OnInit, OnDestroy {

    public static PARAMETERACCESSCONTROL = 'accesscontrolid';
    public static PARAMETERADDRESS = 'addressid';
    public static PARAMETERGATE = 'gateid';
    public static PARAMETERLEFTOPEN = 'columnleftview';
    public static PARAMETERFILTEROPTION = 'filteroption';
    public static PARAMETERGATEIDS = 'gateids';
    public static PARAMETERPHASE = 'phaseid';

    public addressAccessControl: AddressAccessControlSelectorDto | null = null;
    public filter: ForkliftFilterDto | null = null;
    public value: ForkliftDto | null = null;
    public columnLeftView: number = 0;
    public openGateIds: string[] = [];
    public selectedGateId: string | null = null;
    private selectedPeriodId: string | null = null;
    public selectedRoute: AccessControlGatePlateDto | null = null;
    public selectedPhaseId: number | null = null;
    public actionsWidth: number = 200;

    private filterChange$ = new Subject<ForkliftFilterDto>();
    private destroy$ = new Subject<void>();
    public searchText: string = "";

    constructor(private activatedRoute: ActivatedRoute, private router: Router,
        private accessControlController: AccessControlController,
        private userSettingController: UserSettingController,
        @Inject('HomeController') protected homeController: HomeController,
        @Inject('SharedAsideFactory') public sharedAsideFactory: SharedAsideFactory) { }

    ngOnInit(): void {
        this.filterChange$.pipe(
            debounceTime(1000),
            distinctUntilChanged((a, b) => ForkliftFilterDto.equals(a, b)),
            takeUntil(this.destroy$)
        ).subscribe(filter => this.loadData(filter));

        this.loadParameters();

        this.homeController.onForkliftChanged$.subscribe(accesscontrolid => {
            if (!HLong.isNullOrNullLong(accesscontrolid) && this.filter != null && !HLong.isNullOrNullLong(this.filter.accessControlId) && accesscontrolid == this.filter.accessControlId) {
                this.loadData(this.filter);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadParameters() {
        this.userSettingController.getForkliftScreen().subscribe(screen => {
            const params = this.activatedRoute.snapshot.queryParamMap;
            const rawAC = params.get(SharedForkliftComponent.PARAMETERACCESSCONTROL);
            const rawAddr = params.get(SharedForkliftComponent.PARAMETERADDRESS);
            const rawGate = params.get(SharedForkliftComponent.PARAMETERGATE);
            const rawLeftOpen = params.get(SharedForkliftComponent.PARAMETERLEFTOPEN);
            const rawPeriod = params.get(SharedForkliftComponent.PARAMETERFILTEROPTION);
            const rawGateIds = params.get(SharedForkliftComponent.PARAMETERGATEIDS);
            const rawPhase = params.get(SharedForkliftComponent.PARAMETERPHASE);

            const accessControlId = HString.isNullOrNullString(rawAC) ? null : +rawAC;
            const addressId = HString.isNullOrNullString(rawAddr) ? null : +rawAddr;

            const resolvedGateId = !HString.isNullOrNullString(rawGate) ? rawGate : (screen?.gateId ?? '');
            const resolvedLeftView = rawLeftOpen !== null
                ? (rawLeftOpen === 'true' ? 1 : rawLeftOpen === 'false' ? 0 : +rawLeftOpen)
                : (screen?.columnLeftView ?? 0);
            const resolvedPeriodId = !HString.isNullOrNullString(rawPeriod) ? rawPeriod : (screen?.periodFilterId ?? null);
            const resolvedGateIds = !HString.isNullOrNullString(rawGateIds)
                ? rawGateIds.split(',').filter(id => id.length > 0)
                : (screen?.openGateIds ?? []);

            const resolvedPhaseId = !HString.isNullOrNullString(rawPhase) ? +rawPhase : (screen?.selectedPhaseId ?? null);

            this.columnLeftView = resolvedLeftView;
            this.openGateIds = resolvedGateIds;
            this.selectedPeriodId = resolvedPeriodId;
            this.selectedPhaseId = resolvedPhaseId;
            this.addressAccessControl = AddressAccessControlSelectorDto.buildStandard('forklift');
            this.addressAccessControl.accessControlId = accessControlId;
            this.addressAccessControl.addressId = addressId;
            this.selectedGateId = resolvedGateId;
            this.updateFilter(accessControlId, addressId, HString.isNullOrNullString(resolvedGateId) ? null : +resolvedGateId);
        });
    }

    onChangeAddressAccessControl(value: AddressAccessControlSelectorDto) {
        const prevAccessControlId = this.filter?.accessControlId ?? null;
        this.addressAccessControl = value;
        const accessControlId = value?.accessControlId ?? null;
        const addressId = value?.addressId ?? null;
        const hadPrevAccessControl = prevAccessControlId != null && prevAccessControlId !== 0;
        if (hadPrevAccessControl && accessControlId !== prevAccessControlId) {
            this.selectedGateId = '';
        }
        const gateId = HString.isNullOrNullString(this.selectedGateId) ? null : +this.selectedGateId;

        this.updateFilter(accessControlId, addressId, gateId);
        this.setParametersToURL();
    }

    private updateFilter(accessControlId: number | null, addressId: number | null, gateId: number | null) {
        this.filter = ForkliftFilterDto.build(accessControlId, addressId, gateId);
        this.applyPeriodSelection();
        this.filterChange$.next(this.filter);
    }

    private applyPeriodSelection() {
        if (!HString.isNullOrNullString(this.selectedPeriodId) && this.filter?.periodFilter)
            OptionsStripDto.selectById(this.filter.periodFilter, this.selectedPeriodId);
        if (this.filter)
            this.filter.selectedPeriodId = this.selectedPeriodId;
    }

    filterChanged(option: OptionDto) {
        this.selectedPeriodId = option?.optionId ?? null;
        this.applyPeriodSelection();
        this.loadData(this.filter);
        this.setParametersToURL();
    }

    private loadData(filter: ForkliftFilterDto | null) {
        if (filter == null)
            return;
        this.accessControlController.getForklift(filter).subscribe(value => {
            this.value = value;
        });
    }

    setParametersToURL() {
        const object: any = {};
        if (this.addressAccessControl)
            if (!HLong.isNullOrNullLong(this.addressAccessControl?.accessControlId))
                object[SharedForkliftComponent.PARAMETERACCESSCONTROL] = this.addressAccessControl.accessControlId;
        if (this.addressAccessControl)
            if (!HLong.isNullOrNullLong(this.addressAccessControl?.addressId))
                object[SharedForkliftComponent.PARAMETERADDRESS] = this.addressAccessControl.addressId;
        if (!HString.isNullOrNullString(this.selectedGateId))
            object[SharedForkliftComponent.PARAMETERGATE] = this.selectedGateId;
        if (this.columnLeftView !== 0)
            object[SharedForkliftComponent.PARAMETERLEFTOPEN] = this.columnLeftView.toString();
        if (!HString.isNullOrNullString(this.selectedPeriodId))
            object[SharedForkliftComponent.PARAMETERFILTEROPTION] = this.selectedPeriodId;
        if (this.openGateIds?.length > 0)
            object[SharedForkliftComponent.PARAMETERGATEIDS] = this.openGateIds.join(',');
        if (this.selectedPhaseId != null && this.selectedPhaseId !== 0)
            object[SharedForkliftComponent.PARAMETERPHASE] = this.selectedPhaseId;

        this.router.navigate(['.', {}], {
            relativeTo: this.activatedRoute,
            queryParams: object,
            queryParamsHandling: ''
        });
        this.saveScreenSetting();
    }

    private saveScreenSetting() {
        const dto = ForkliftScreenDto.buildEmpty();
        dto.gateId = this.selectedGateId;
        dto.columnLeftView = this.columnLeftView;
        dto.periodFilterId = this.selectedPeriodId;
        dto.openGateIds = this.openGateIds;
        dto.selectedPhaseId = this.selectedPhaseId;
        this.userSettingController.setForkliftScreen(dto).subscribe();
    }

    onPhaseChanged(phaseId: number) {
        if (phaseId != null && phaseId !== 0)
            this.selectedPhaseId = phaseId;
        this.setParametersToURL();
    }

    onRightRefresh() {
        this.loadData(this.filter);
    }

    onLeftRefresh() {
        this.loadData(this.filter);
    }

    onColumnLeftViewChange(value: number) {
        this.columnLeftView = value;
        this.setParametersToURL();
    }

    onChangeOpenGateIds(ids: string[]) {
        this.openGateIds = ids;
        this.setParametersToURL();
    }

    onClickRoute(route: AccessControlGatePlateDto) {
        this.selectedRoute = { ...route };
        this.selectedPhaseId = route.phaseId;
        this.setParametersToURL();
    }

    onDblClickRoute(route: AccessControlGatePlateDto) {
        if (route != null)
            this.sharedAsideFactory.invokeActiveRoutePhase(route.routeId, route.phaseId).then(value => {
                this.loadData(this.filter);
            });
    }

    onClickGateOption(option: OptionDto) {
        this.selectedGateId = option?.optionId ?? "";
        const gateId = HString.isNullOrNullString(this.selectedGateId) ? null : +this.selectedGateId;
        this.filter = ForkliftFilterDto.build(this.filter?.accessControlId, this.filter?.addressId, gateId);
        this.applyPeriodSelection();
        this.filterChange$.next(this.filter);
        this.setParametersToURL();
    }

    espai($event: any) {
        $event.stopPropagation();
    }
    public reloadClick($event: any) {
        $event.stopPropagation();
        if (this.filter != null)
            this.loadData(this.filter);
    }
    public pretty(value: any): string {
        return JSON.stringify(value);
    }
}
